/**
 * AppTopBar — the 56px phone navigation bar (Phase 14.1).
 *
 * Rendered by `AppRail` below the `sm` breakpoint as the phone replacement
 * for the icon rail: hamburger (opens `NavDrawer`) · module title · optional
 * action · avatar/user menu. Desktop and tablet never see it (`sm:hidden`).
 *
 * Source: .agents/design/responsive/responsive-app.jsx `.topbar` / `.hb`.
 */

import type { ReactNode } from 'react';
import { UserMenuBrand } from './AppShell';

interface AppTopBarProps {
  /** Active module label, e.g. 'Kontakty'. */
  title: string;
  onMenuClick: () => void;
  /** Optional right-aligned action (icon button) before the avatar. */
  action?: ReactNode;
  /** Fallback brand letter forwarded to the avatar menu. */
  brand?: string;
  /** Avatar slot override — defaults to the shared `UserMenuBrand`. Stories
   * and anonymous shells can pass a plain mark to avoid the auth queries. */
  avatar?: ReactNode;
}

export function AppTopBar({ title, onMenuClick, action, brand = 'A', avatar }: AppTopBarProps) {
  return (
    <header className="flex h-14 shrink-0 items-center gap-3 border-b border-line bg-paper px-4 sm:hidden">
      <button
        type="button"
        aria-label="Otevřít navigaci"
        onClick={onMenuClick}
        className="-ml-2 grid size-10 cursor-pointer place-items-center text-ink"
      >
        <span aria-hidden className="flex flex-col gap-1">
          <span className="block h-[1.6px] w-[18px] bg-current" />
          <span className="block h-[1.6px] w-[18px] bg-current" />
          <span className="block h-[1.6px] w-[18px] bg-current" />
        </span>
      </button>
      <div className="min-w-0 truncate font-mono text-[11px] uppercase tracking-[0.14em] text-ink-3">
        Modul · <b className="font-semibold text-ink">{title}</b>
      </div>
      <div className="flex-1" />
      {action}
      {avatar ?? <UserMenuBrand fallbackLetter={brand} className="" />}
    </header>
  );
}
